/**
 * Renders a `crm_table` view action (design D4): a live, filterable table of
 * CRM companies or persons whose rows this component loads itself through
 * the generic `agentViewData(component: "crm_table", params)` GraphQL query,
 * so the model never invents or repeats rows. The tool only describes the
 * view (title, source, columns, editable filters); everything else —
 * paging, filtering, row links — lives here.
 */
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { DebouncedSearchInput, Switch } from "@msys/ui";
import * as Sentry from "@sentry/react";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  AgentCrmViewDataDocument,
  type AgentCrmViewDataQuery,
  type AgentCrmViewDataQueryVariables,
} from "./crm.generated.js";
import type { AgentViewComponent, AgentViewSpec } from "./registry.js";

// --- payload types (mirrors the backend's crmViews.ts, design D4) ---------

export type CrmCompaniesSource = {
  kind: "crm_companies";
  search?: string;
  contactType?: Array<"COMPANY" | "INDIVIDUAL">;
  abcCategory?: Array<"a" | "b" | "c" | "none">;
  tagsAny?: string[];
  partnersOnly?: boolean;
  sortBy: "companyName" | "createdAt" | "crmNumber";
  sortDirection: "asc" | "desc";
};

export type CrmPersonsSource = {
  kind: "crm_persons";
  search?: string;
  tagsAny?: string[];
  crmCompanyId?: string;
  sortBy: "familyname" | "firstname";
  sortDirection: "asc" | "desc";
};

export type CrmViewSource = CrmCompaniesSource | CrmPersonsSource;

export type CrmTableViewPayload = {
  component: "crm_table";
  title: string;
  description: string | null;
  source: CrmViewSource;
  columns: string[];
  editableFilters: string[];
  pageSize: number;
};

export type CrmViewRow = { id: string; url: string } & Record<
  string,
  string | number | boolean | string[] | null
>;

// --- type guard (no `as`, no `any`, no `!`) -------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(item => typeof item === "string");
}

function isOneOf<T extends string>(
  value: unknown,
  allowed: readonly T[]
): value is T {
  return (
    typeof value === "string" && (allowed as readonly string[]).includes(value)
  );
}

function isArrayOf<T extends string>(
  value: unknown,
  allowed: readonly T[]
): value is T[] {
  return Array.isArray(value) && value.every(item => isOneOf(item, allowed));
}

const CONTACT_TYPES = ["COMPANY", "INDIVIDUAL"] as const;
const ABC_CATEGORIES = ["a", "b", "c", "none"] as const;
const SORT_DIRECTIONS = ["asc", "desc"] as const;
const COMPANIES_SORT_BY = ["companyName", "createdAt", "crmNumber"] as const;
const PERSONS_SORT_BY = ["familyname", "firstname"] as const;

function isCrmCompaniesSource(
  value: Record<string, unknown>
): value is CrmCompaniesSource {
  return (
    value.kind === "crm_companies" &&
    (value.search === undefined || typeof value.search === "string") &&
    (value.contactType === undefined ||
      isArrayOf(value.contactType, CONTACT_TYPES)) &&
    (value.abcCategory === undefined ||
      isArrayOf(value.abcCategory, ABC_CATEGORIES)) &&
    (value.tagsAny === undefined || isStringArray(value.tagsAny)) &&
    (value.partnersOnly === undefined ||
      typeof value.partnersOnly === "boolean") &&
    isOneOf(value.sortBy, COMPANIES_SORT_BY) &&
    isOneOf(value.sortDirection, SORT_DIRECTIONS)
  );
}

function isCrmPersonsSource(
  value: Record<string, unknown>
): value is CrmPersonsSource {
  return (
    value.kind === "crm_persons" &&
    (value.search === undefined || typeof value.search === "string") &&
    (value.tagsAny === undefined || isStringArray(value.tagsAny)) &&
    (value.crmCompanyId === undefined ||
      typeof value.crmCompanyId === "string") &&
    isOneOf(value.sortBy, PERSONS_SORT_BY) &&
    isOneOf(value.sortDirection, SORT_DIRECTIONS)
  );
}

function isCrmViewSource(value: unknown): value is CrmViewSource {
  return (
    isRecord(value) &&
    (isCrmCompaniesSource(value) || isCrmPersonsSource(value))
  );
}

export function isCrmTableView(
  view: AgentViewSpec
): view is CrmTableViewPayload {
  return (
    view.component === "crm_table" &&
    typeof view.title === "string" &&
    (view.description === null || typeof view.description === "string") &&
    isCrmViewSource(view.source) &&
    isStringArray(view.columns) &&
    isStringArray(view.editableFilters) &&
    typeof view.pageSize === "number"
  );
}

// --- data fetching ----------------------------------------------------------

type CrmViewPage = { totalCount: number; rows: CrmViewRow[] };

function isCrmViewRow(value: unknown): value is CrmViewRow {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.url === "string"
  );
}

/** Parses the opaque `AgentViewDataResult.data` JSON payload (no `any`, no `as`). */
function parseCrmViewPage(raw: unknown): CrmViewPage | null {
  if (
    !isRecord(raw) ||
    typeof raw.totalCount !== "number" ||
    !Array.isArray(raw.rows) ||
    !raw.rows.every(isCrmViewRow)
  ) {
    return null;
  }
  return { totalCount: raw.totalCount, rows: raw.rows };
}

type CrmViewDataState = {
  rows: CrmViewRow[];
  totalCount: number;
  loading: boolean;
  error: boolean;
};

function useCrmViewData(
  source: CrmViewSource,
  offset: number,
  limit: number
): CrmViewDataState {
  const apolloClient = useApolloClient();
  const [state, setState] = React.useState<CrmViewDataState>({
    rows: [],
    totalCount: 0,
    loading: true,
    error: false,
  });
  const sourceKey = JSON.stringify(source);

  React.useEffect(() => {
    let cancelled = false;
    setState(current => ({ ...current, loading: true, error: false }));
    apolloClient
      .query<AgentCrmViewDataQuery, AgentCrmViewDataQueryVariables>({
        query: AgentCrmViewDataDocument,
        variables: { params: { source, offset, limit } },
        fetchPolicy: "network-only",
        // This view can be re-rendered from chat history after the caller's
        // assistant access was revoked; it already shows its own error state
        // (below), so the global Apollo error link must not also toast.
        context: { noErrorNotification: true },
      })
      .then(({ data }) => {
        if (cancelled) return;
        const result = getDataOrNull(data.agentViewData);
        const page = result ? parseCrmViewPage(result.data) : null;
        if (page) {
          setState({ ...page, loading: false, error: false });
        } else {
          setState(current => ({ ...current, loading: false, error: true }));
        }
      })
      .catch((err: unknown) => {
        Sentry.captureException(err);
        if (!cancelled)
          setState(current => ({ ...current, loading: false, error: true }));
      });
    return () => {
      cancelled = true;
    };
    // `sourceKey` is the intentional proxy for `source` (a new object every
    // render); the effect always reads the current render's `source`.
    // `apolloClient` is stable in the real app (a single context value) and
    // is deliberately left out so re-running never depends on its identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceKey, offset, limit]);

  return state;
}

// --- table ------------------------------------------------------------------

function renderCellValue(
  value: CrmViewRow[string],
  yesLabel: string
): React.ReactNode {
  if (value === null || value === undefined || value === "") return "–";
  if (Array.isArray(value)) {
    return value.length === 0 ? (
      "–"
    ) : (
      <Stack
        direction="row"
        spacing={0.5}
        sx={{ flexWrap: "wrap", rowGap: 0.5 }}
      >
        {value.map(tag => (
          <Chip key={tag} size="small" label={tag} />
        ))}
      </Stack>
    );
  }
  if (typeof value === "boolean") return value ? yesLabel : "–";
  return String(value);
}

function CrmTable({ view }: { view: CrmTableViewPayload }) {
  const { t } = useTranslate([
    "Assistant",
    "Global",
    "CrmOrganisations",
    "OrganisationProfile",
    "UserProfile",
  ]);
  const [source, setSource] = React.useState<CrmViewSource>(view.source);
  const [page, setPage] = React.useState(0);
  const pageSize = view.pageSize;
  const { rows, totalCount, loading, error } = useCrmViewData(
    source,
    page * pageSize,
    pageSize
  );
  const editable = new Set(view.editableFilters);

  const columnLabels: Record<string, string> = {
    name: t("Name", { ns: "Global" }),
    number: t("No.", { ns: "Global" }),
    contactType: t("Type", { ns: "Global" }),
    abcCategory: t("ABC category", { ns: "CrmOrganisations" }),
    email: t("Email", { ns: "Global" }),
    phone: t("Phone", { ns: "Global" }),
    postalCode: t("Postal code", { ns: "Global" }),
    city: t("City", { ns: "Global" }),
    tags: t("Tags", { ns: "CrmOrganisations" }),
    partner: t("Partner", { ns: "CrmOrganisations" }),
    company: t("Company", { ns: "OrganisationProfile" }),
    jobTitle: t("Job title", { ns: "UserProfile" }),
  };
  const yesLabel = t("Yes", { ns: "Global" });

  const setSearch = (value: string) => {
    setSource(current => ({
      ...current,
      search: value.trim() === "" ? undefined : value,
    }));
    setPage(0);
  };

  const setTagsAny = (tags: string[]) => {
    setSource(current => ({
      ...current,
      tagsAny: tags.length > 0 ? tags : undefined,
    }));
    setPage(0);
  };

  const toggleContactType = (type: "COMPANY" | "INDIVIDUAL") => {
    setSource(current => {
      if (current.kind !== "crm_companies") return current;
      const list = current.contactType ?? [];
      const next = list.includes(type)
        ? list.filter(item => item !== type)
        : [...list, type];
      return { ...current, contactType: next.length > 0 ? next : undefined };
    });
    setPage(0);
  };

  const toggleAbcCategory = (category: "a" | "b" | "c") => {
    setSource(current => {
      if (current.kind !== "crm_companies") return current;
      const list = current.abcCategory ?? [];
      const next = list.includes(category)
        ? list.filter(item => item !== category)
        : [...list, category];
      return { ...current, abcCategory: next.length > 0 ? next : undefined };
    });
    setPage(0);
  };

  const setPartnersOnly = (checked: boolean) => {
    setSource(current =>
      current.kind === "crm_companies"
        ? { ...current, partnersOnly: checked ? true : undefined }
        : current
    );
    setPage(0);
  };

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">{view.title}</Typography>
      {view.description && (
        <Typography variant="body2" color="text.secondary">
          {view.description}
        </Typography>
      )}

      {editable.size > 0 && (
        <Stack
          direction="row"
          spacing={1}
          sx={{ flexWrap: "wrap", rowGap: 1, alignItems: "center" }}
        >
          {editable.has("search") && (
            <DebouncedSearchInput
              placeholder={t("Search", { ns: "Global" })}
              defaultValue={source.search ?? ""}
              onChangeSearchTerm={setSearch}
            />
          )}
          {source.kind === "crm_companies" &&
            editable.has("contactType") &&
            (["COMPANY", "INDIVIDUAL"] as const).map(type => (
              <Chip
                key={type}
                label={
                  type === "COMPANY"
                    ? t("Company", { ns: "OrganisationProfile" })
                    : t("Individual", { ns: "OrganisationProfile" })
                }
                color={
                  source.contactType?.includes(type) ? "primary" : "default"
                }
                onClick={() => toggleContactType(type)}
              />
            ))}
          {source.kind === "crm_companies" &&
            editable.has("abcCategory") &&
            (["a", "b", "c"] as const).map(category => (
              <Chip
                key={category}
                label={category.toUpperCase()}
                color={
                  source.abcCategory?.includes(category) ? "primary" : "default"
                }
                onClick={() => toggleAbcCategory(category)}
              />
            ))}
          {editable.has("tagsAny") && (
            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={[]}
              value={source.tagsAny ?? []}
              onChange={(_event, value) => setTagsAny(value)}
              renderInput={params => (
                <TextField
                  {...params}
                  label={t("Tags", { ns: "CrmOrganisations" })}
                  sx={{ minWidth: 200 }}
                />
              )}
            />
          )}
          {source.kind === "crm_companies" && editable.has("partnersOnly") && (
            <Switch
              label={t("Partners only")}
              checked={source.partnersOnly === true}
              onChange={event => setPartnersOnly(event.target.checked)}
            />
          )}
        </Stack>
      )}

      {error && <Alert severity="error">{t("Could not load the data")}</Alert>}

      <Box sx={{ overflowX: "auto", position: "relative" }}>
        {loading && (
          <CircularProgress
            size={16}
            sx={{ position: "absolute", right: 8, top: 8 }}
          />
        )}
        <Table size="small">
          <TableHead>
            <TableRow>
              {view.columns.map(column => (
                <TableCell key={column}>
                  {columnLabels[column] ?? column}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.id} hover>
                {view.columns.map((column, columnIndex) => (
                  <TableCell key={column}>
                    {columnIndex === 0 ? (
                      <Link component={RouterLink} to={row.url}>
                        {renderCellValue(row[column], yesLabel)}
                      </Link>
                    ) : (
                      renderCellValue(row[column], yesLabel)
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {!loading && !error && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={view.columns.length}>
                  <Typography variant="body2" color="text.secondary">
                    {t("No results")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        rowsPerPage={pageSize}
        rowsPerPageOptions={[]}
        onPageChange={(_event, next) => setPage(next)}
      />
    </Stack>
  );
}

// --- registration -----------------------------------------------------------

export const CrmTableView: AgentViewComponent = ({ view }) => {
  if (!isCrmTableView(view)) return null;
  return <CrmTable view={view} />;
};

export const crmAgentViews: Record<string, AgentViewComponent> = {
  crm_table: CrmTableView,
};
